/**
 * Gateway Power lockup: the operating entity's current website brand, as
 * exported on 2026-09-29 from its site's Navigation.tsx (desktop proportions:
 * 25px geometric G mark, 12px gap, 15px Plus Jakarta Sans lettering at
 * 0.14em; GATEWAY weight 700, POWER weight 400 in neutral-300).
 *
 * Vector only. The mark is one path split by two clip-paths (ink in the
 * upper-left triangle, brand teal in the lower-right); the wordmark letters
 * are outlined (SIL OFL font), so no web font loads for it. Colours come
 * from the marketing tokens: on the dark theme they are the brand's
 * white-on-navy palette, on the light theme the brand's light-background
 * artwork (brand navy ink, neutral-600 POWER, teal-600 mark). The mark and
 * the wordmark are separate <svg>s so the header can drop the wordmark where
 * the inline links need the room.
 *
 * Source: brand/gateway-power/ (the 2026-09-29 export and its light-background
 * variants: README.txt, the SVGs and the font licence); legalEntity.test.tsx
 * pins this file to it.
 */
import React, { useId } from 'react';

const MARK_PATH = 'M8 12H80V26H22V70H66V55H44V51L54 41H80V84H8Z';
const MARK_CLIP_INK = '0,0 89,0 0,89';
const MARK_CLIP_TEAL = '0,95 95,0 120,0 120,120 0,120';

/** [x offset in wordmark units, ink, outlined glyph path] — GATEWAY then POWER. */
const WORDMARK_GLYPHS: ReadonlyArray<readonly [number, 'primary' | 'secondary', string]> = [
  [0, 'primary', 'M425 -12Q345.25 -12 277.0 16.875Q208.75 45.75 157.625 97.625Q106.5 149.5 78.0 219.5Q49.5 289.5 49.5 372.75Q49.5 456 77.25 526.375Q105 596.75 155.375 648.375Q205.75 700 273.625 728.5Q341.5 757 422 757Q501.75 757 565.25 730.0Q628.75 703 672.875 658.625Q717 614.25 735.75 561.25L615 503Q593.75 560.5 543.625 596.875Q493.5 633.25 422 633.25Q352 633.25 298.5 600.25Q245 567.25 215.375 509.0Q185.75 450.75 185.75 372.75Q185.75 295 216.5 236.25Q247.25 177.5 301.125 144.625Q355 111.75 425 111.75Q481.5 111.75 529.5 134.375Q577.5 157 606.625 198.625Q635.75 240.25 635.75 297.25V354L698 300H422V415H772V340.25Q772 253.75 743.0 187.75Q714 121.75 664.625 77.125Q615.25 32.5 553.0 10.25Q490.75 -12 425 -12Z'],
  [14.115, 'primary', 'M11 0 268 745H444L701 0H553.25L499.75 159.5H211.75L158 0ZM250.5 279.5H460.5L337.25 650.5H374.75Z'],
  [26.145, 'primary', 'M204.25 0V625H11V745H531V625H340.5V0Z'],
  [36.375, 'primary', 'M71.5 0V745H567.25V625H207.75V433.5H547.25V313.5H207.75V120H567.25V0Z'],
  [47.4, 'primary', 'M210.75 0 11 745H157.25L313.75 110.25H275.75L439.5 745H580.75L744.25 110.25H706L862.75 745H1009L809.25 0H653L488.25 617.5H531.75L367 0Z'],
  [64.2, 'primary', 'M11 0 268 745H444L701 0H553.25L499.75 159.5H211.75L158 0ZM250.5 279.5H460.5L337.25 650.5H374.75Z'],
  [75.93, 'primary', 'M262.25 0V330.25L11 745H166L358 421.25H302.75L493.75 745H648.75L398.5 331V0Z'],
  [92.58, 'secondary', 'M88 0V745H344Q417 745 472.0 716.5Q527 688 557.5 636.5Q588 585 588 517Q588 449 557.5 398.0Q527 347 472.5 318.5Q418 290 344 290H168V0ZM168 365H348Q396 365 432.0 383.5Q468 402 488.0 436.5Q508 471 508 518Q508 565 488.0 599.0Q468 633 432.0 651.5Q396 670 348 670H168Z'],
  [104.325, 'secondary', 'M439 -12Q361 -12 293.5 16.5Q226 45 175.0 96.5Q124 148 95.0 218.5Q66 289 66 373Q66 457 95.0 527.5Q124 598 175.0 649.5Q226 701 293.5 729.0Q361 757 439 757Q517 757 584.5 728.5Q652 700 703.0 648.5Q754 597 783.0 527.0Q812 457 812 373Q812 289 783.0 218.5Q754 148 703.0 96.5Q652 45 584.5 16.5Q517 -12 439 -12ZM439 63Q504 63 557.5 87.0Q611 111 650.0 154.0Q689 197 710.5 253.0Q732 309 732 373Q732 436 710.5 492.0Q689 548 650.0 591.0Q611 634 557.5 658.0Q504 682 439 682Q374 682 320.5 658.0Q267 634 228.0 591.0Q189 548 167.5 492.0Q146 436 146 373Q146 309 167.5 253.0Q189 197 228.0 154.0Q267 111 321.0 87.0Q375 63 439 63Z'],
  [119.295, 'secondary', 'M225 0 11 745H92L277 78H260L450 745H535L724 78H706L892 745H973L759 0H674L483 670H501L310 0Z'],
  [136.155, 'secondary', 'M88 0V745H568V670H168V411H548V336H168V75H568V0Z'],
  [147.435, 'secondary', 'M88 0V745H344Q417 745 472.0 716.5Q527 688 557.5 636.5Q588 585 588 517Q588 438 546.5 382.0Q505 326 434 303L608 0H514L332 321L386 290H168V0ZM168 365H348Q396 365 432.0 383.5Q468 402 488.0 436.5Q508 471 508 518Q508 565 488.0 599.0Q468 633 432.0 651.5Q396 670 348 670H168Z'],
];

const WORDMARK_VIEW_BOX = '-0.757500 5.765000 158.812500 14.535000';

function svgSafeId(raw: string, suffix: string): string {
  return `gw-${raw.replace(/[^a-zA-Z0-9_-]/g, '')}-${suffix}`;
}

export interface GatewayPowerMarkProps {
  className?: string;
  /**
   * Inside a link that already carries the name (see `GatewayPowerLink`) the
   * mark is decorative; on its own it is an image named "Gateway Power".
   */
  decorative?: boolean;
}

/** The geometric G mark alone (square). */
export function GatewayPowerMark({ className, decorative = false }: GatewayPowerMarkProps): React.JSX.Element {
  const id = useId();
  const inkClip = svgSafeId(id, 'ink');
  const tealClip = svgSafeId(id, 'teal');
  const naming = decorative ? { 'aria-hidden': true as const } : { role: 'img', 'aria-label': 'Gateway Power' };
  return (
    <svg className={className} viewBox="8 12 72 72" focusable="false" {...naming}>
      <defs>
        <clipPath id={inkClip}><polygon points={MARK_CLIP_INK} /></clipPath>
        <clipPath id={tealClip}><polygon points={MARK_CLIP_TEAL} /></clipPath>
      </defs>
      <path d={MARK_PATH} fill="var(--dc-gw-ink)" clipPath={`url(#${inkClip})`} />
      <path d={MARK_PATH} fill="var(--dc-gw-teal)" clipPath={`url(#${tealClip})`} />
    </svg>
  );
}

/** The outlined GATEWAY POWER lettering alone; decorative next to the mark. */
export function GatewayPowerWordmark({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox={WORDMARK_VIEW_BOX} aria-hidden="true" focusable="false">
      {WORDMARK_GLYPHS.map(([x, ink, d]) => (
        <path
          key={x}
          transform={`translate(${x} 18.62) scale(0.015 -0.015)`}
          fill={ink === 'primary' ? 'var(--dc-gw-ink)' : 'var(--dc-gw-ink2)'}
          d={d}
        />
      ))}
    </svg>
  );
}

/**
 * Mark + wordmark side by side, sized and placed by the `.dc-gw-*` rules to
 * the brand lockup's proportions. Decorative: the enclosing link names it.
 */
export function GatewayPowerLockup({ className }: { className?: string }): React.JSX.Element {
  return (
    <span className={className ? `dc-gw-lockup ${className}` : 'dc-gw-lockup'}>
      <GatewayPowerMark className="dc-gw-mark" decorative />
      <GatewayPowerWordmark className="dc-gw-word" />
    </span>
  );
}
